import { APP_BASE_HREF, Location, AsyncPipe } from "@angular/common";
import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { Observable } from "rxjs";

import { SvgIconComponent } from "@hermes/aphrodite-svg";
import {
    Context,
    LOCALE,
    StorageManager,
    StorageService,
} from "@hermes/core-app-core";
import { AnalyticsService } from "@hermes/hermes-web-fragments-analytics";
import { ACCOUNT_PATH, LOGIN_PATH } from "@hermes/hermes-web-fragments-customer";
import { UserFacade } from "@hermes/hermes-web-states-user-state";
import { SrOnlyNoSnippetDirective } from "@hermes/utils-generic-directives-sr-only-no-snippet";
import { RouterService } from "@hermes/utils-generic-services-router";

import { MenuFooterClickEvent } from "./category-institutional-click.event";

@Component({
    selector: "h-category-institutional",
    imports: [
        SvgIconComponent,
        SrOnlyNoSnippetDirective,
        AsyncPipe,
        TranslateModule,
    ],
    templateUrl: "./category-institutional.component.html",
    styleUrls: ["./category-institutional.component.scss"],
})
export class CategoryInstitutionalComponent {
    public baseHref = inject(APP_BASE_HREF);
    private locale = inject(LOCALE);
    private routerService = inject(RouterService);
    private userFacade = inject(UserFacade);
    private location = inject(Location);
    private context = inject(Context);
    private analyticsService = inject(AnalyticsService);
    private readonly storageService = inject(StorageService);

    @ViewChild("transFindastore")
    public transFindastore!: ElementRef;
    @ViewChild("transAccount")
    public transAccount!: ElementRef;
    @ViewChild("transHelp")
    public transHelp!: ElementRef;

    public findAStoreUrl = `${this.locale.urlPrefix}/find-store/`;
    public accountUrl = ACCOUNT_PATH;
    public contactUsUrl = "/contact-us/";
    public loginUrl = LOGIN_PATH;
    public isLoggedIn$: Observable<boolean> = this.userFacade.isLoggedIn$;

    private sessionStorage?: StorageManager;

    constructor() {
        this.sessionStorage = this.storageService.getSessionStorageInstance();
    }

    public sendAccountEvent($event: Event): void {
        this.sendMenuEvent(
            $event,
            "Account",
            this.transAccount?.nativeElement.textContent,
        );
    }

    public sendContactUsEvent($event: Event): void {
        this.sendMenuEvent(
            $event,
            "ContactUs",
            this.transHelp?.nativeElement.textContent,
        );
    }

    public navigateOnShopPage($event: Event): void {
        $event.preventDefault();

        this.sendFindAStoreEvent($event);

        this.routerService.navigate(this.findAStoreUrl);
    }

    public sendFindAStoreEvent($event: Event): void {
        this.sendMenuEvent(
            $event,
            "FindaStore",
            this.transFindastore?.nativeElement.textContent,
        );
    }

    public sendMenuEvent(
        $event: Event,
        label: string,
        translation: string,
    ): void {
        $event.stopPropagation();
        this.sendMenuFooterEvent({ label, translation });
    }

    public goToPage(event: MouseEvent, path: string): void {
        event.preventDefault();
        if (this.location.path() === path) {
            // If we already are on page, we must reload it, so we use nativeRedirect
            this.routerService.nativeRedirect(
                `${this.locale.urlPrefix}${path}`,
            );
            return;
        }
        // else we use spaRedirect
        this.routerService.spaRedirect(path);
    }

    /**
     * function sendMenuFooterEvent
     *
     * send analytics data for main menu
     */
    public sendMenuFooterEvent(params: {
        noLabel?: boolean;
        label?: string;
        translation?: string;
        action?: string;
    }): void {
        if (this.context.isInBrowserMode()) {
            const navtoStore = params.noLabel
                ? params.label
                : `${params.translation}/${params.translation}`;
            const label = params.noLabel ? undefined : params.label;
            const action =
                params.action === "aftersales"
                    ? "Aftersales"
                    : "Maison_Hermes_footer";
            this.sessionStorage?.setItem("nav", navtoStore);
            this.analyticsService.sendData(
                new MenuFooterClickEvent({ label, action }),
            );
        }
    }
}
